/**
 * SSO: Central Mango sign-in for business frontends.
 * When a business sends users here with ?return_url=..., we either:
 * - If already logged in: redirect back to return_url#mango_token=JWT (no form).
 * - If not: show login, then redirect back with token so they're logged in on that business too.
 * This gives "sign in once, use on any business frontend" (cross-domain SSO).
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const TOKEN_KEY = 'mangoPersonalToken';

/** Allowed return_url origins for SSO redirect. Add business frontend domains as needed. */
const ALLOWED_ORIGINS = [
  typeof window !== 'undefined' ? window.location.origin : '',
  'http://localhost:3000',
  'http://localhost:3001',
  'http://127.0.0.1:3000',
  'http://127.0.0.1:3001',
  'https://rainbow-sails.shop',
  'https://www.rainbow-sails.shop',
  'https://shop.rainbow-sails.shop',
  'https://rainbowsales.shop',
  'https://www.rainbowsales.shop',
  'https://shop.rainbowsales.shop',
  'https://b2p.productions',
  'https://www.b2p.productions',
  'https://back2people.com.au',
  'https://www.back2people.com.au',
  'https://personal.mango-business.solutions',
];

function isValidReturnUrl(url: string): boolean {
  try {
    const u = new URL(url);
    const origin = u.origin;
    return ALLOWED_ORIGINS.some(allowed => allowed && origin === allowed);
  } catch {
    return false;
  }
}

const SSO: React.FC = () => {
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get('return_url') || '';
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    if (!returnUrl || !isValidReturnUrl(returnUrl)) {
      return;
    }
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      setRedirecting(true);
      const base = returnUrl.replace(/#.*$/, '');
      const target = `${base}#mango_token=${encodeURIComponent(token)}`;
      window.location.replace(target);
    }
  }, [returnUrl]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!returnUrl || !isValidReturnUrl(returnUrl)) {
      setError('Invalid return URL');
      return;
    }
    setLoading(true);
    setError('');
    const ok = await login(email, password);
    setLoading(false);
    if (ok) {
      const token = localStorage.getItem(TOKEN_KEY);
      if (token) {
        setRedirecting(true);
        const base = returnUrl.replace(/#.*$/, '');
        const target = `${base}#mango_token=${encodeURIComponent(token)}`;
        window.location.replace(target);
      } else {
        setError('Logged in but no token – try again');
      }
    } else {
      setError('Invalid email or password');
    }
  };

  if (!returnUrl) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #ff9a56 0%, #ff6b35 100%)', padding: 20 }}>
        <div style={{ background: 'white', padding: 32, borderRadius: 16, maxWidth: 400, textAlign: 'center' }}>
          <p style={{ margin: 0, color: '#666' }}>Missing <code>return_url</code>. Use this page from a business site’s “Sign in with Mango” link.</p>
        </div>
      </div>
    );
  }

  if (!isValidReturnUrl(returnUrl)) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #ff9a56 0%, #ff6b35 100%)', padding: 20 }}>
        <div style={{ background: 'white', padding: 32, borderRadius: 16, maxWidth: 400, textAlign: 'center' }}>
          <p style={{ margin: 0, color: '#c62828' }}>Return URL not allowed.</p>
        </div>
      </div>
    );
  }

  if (redirecting) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #ff9a56 0%, #ff6b35 100%)', padding: 20 }}>
        <div style={{ background: 'white', padding: 40, borderRadius: 16, textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🥭</div>
          <p style={{ margin: 0, color: '#333', fontSize: 18 }}>Taking you back...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #ff9a56 0%, #ff6b35 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: 'white', padding: 40, borderRadius: 20, width: '100%', maxWidth: 420, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <span style={{ fontSize: 48 }}>🥭</span>
          <h1 style={{ margin: '12px 0 4px', fontSize: 24, color: '#333' }}>Sign in with Mango</h1>
          <p style={{ margin: 0, color: '#666', fontSize: 14 }}>Use your Mango account to sign in to this site</p>
        </div>
        {error && (
          <div style={{ padding: 12, background: '#ffebee', color: '#c62828', borderRadius: 10, marginBottom: 20, fontSize: 14 }}>{error}</div>
        )}
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 14, color: '#333' }}>Email or Mango ID</label>
            <input
              type="text"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Email or Mango ID"
              style={{ width: '100%', padding: 12, border: '2px solid #e0e0e0', borderRadius: 10, fontSize: 16, boxSizing: 'border-box' }}
            />
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 14, color: '#333' }}>Password</label>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Password"
              style={{ width: '100%', padding: 12, border: '2px solid #e0e0e0', borderRadius: 10, fontSize: 16, boxSizing: 'border-box' }}
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            style={{ width: '100%', padding: 14, background: loading ? '#ccc' : 'linear-gradient(135deg, #ff9a56 0%, #ff6b35 100%)', color: 'white', border: 'none', borderRadius: 10, fontSize: 16, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer' }}
          >
            {loading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default SSO;
